import React, { useEffect, useState } from 'react'

import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  SortDirection,
  UserPermission,
  useNotify
} from '@globaleris/ui.library'
import { useTranslation } from 'react-i18next'

import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'

import {
  getActiveCommodities,
  getActiveCommodity,
  getCustomerDomain,
  storeActiveCommodity,
  storeCommodityFilterForCurrentPage,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage,
  storeShowOnlyActivesFilterForCurrentPage
} from '../../../../redux/helpers'

import { faMoneyCheckDollar } from '@fortawesome/free-solid-svg-icons'
import { EnumCommodity, EnumCurrency, EnumEstadoFactura } from '../../../../api'
import { EnumClientTypes } from '../../../../api/Argentia/enums/enumClientTypes'
import { ConceptModificationType } from '../../enums/conceptModificationType.enum'
import { getUserPermissions } from '../../../../helpers/permissionsHelper'
import { useHttpError } from '../../../../hooks/useHttpError'
import { Pages } from '../../../../model/RBAC/constants'
import { DataTableFilter, RootStoreModel } from '../../../../redux'

import { useSelector } from 'react-redux'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormControlLabel, Grid, Switch, Typography } from '@material-ui/core'
import { Block, CheckCircleOutline, Edit, Tune } from '@material-ui/icons'
import { useHistory } from 'react-router-dom'
import CommodityFilter from '../../../../components/shared/commodityFilter/commodityFilter'
import { enumToSelectOptionArray, enumValueToKey } from '../../../../helpers'
import { useConfirmationDialog, usePageState } from '../../../../hooks'
import { NonAuthRoutes } from '../../../../model'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { mapProductDetail } from '../../products.mapper'
import { ProductDetail, ProductsTableModel } from '../../products.Model'
import { fetchProducts, updateProductDetail } from '../../products.service'
export type ProductsTableConfig = {
  elevation?: number
  rowsPerPage?: number
  rowsPerPageOptions?: number[]
  queryInvoiceStates?: EnumEstadoFactura[] | null
  hideTableColumns?: string[]
}

const defaultTableConfig: ProductsTableConfig = {
  elevation: 1,
  rowsPerPage: 10,
  rowsPerPageOptions: undefined,
  queryInvoiceStates: null
}

export interface ProductsTableProps {
  clientType: EnumClientTypes
  dataTableFilter: DataTableFilter | undefined
  customFilter: any | undefined
  dataTableSearch?: string | undefined
  onFetching?: (isFetching: boolean) => void
  config?: ProductsTableConfig
}

export const ProductsTable: React.FC<ProductsTableProps> = ({ clientType, ...props }) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'products'])
  const history = useHistory()

  const tableConfiguration = props.config || defaultTableConfig

  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )
  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )
  const { dataTableFilter, dataTableSearch, showOnlyActivesFilter } = usePageState()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const commodities = getActiveCommodities()
  const initialCommodityFilter: EnumCommodity =
    getActiveCommodity() ??
    (commodities !== undefined && commodities?.length > 0
      ? enumValueToKey(EnumCommodity, commodities[0])
      : EnumCommodity.GasNatural)

  const [showOnlyActives, setShowOnlyActives] = useState<boolean>(
    showOnlyActivesFilter === undefined ? true : showOnlyActivesFilter
  )
  const [commodity, setCommodity] = useState<EnumCommodity>(initialCommodityFilter)
  const [products, setProducts] = useState<ProductsTableModel[] | undefined>(undefined)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const { info, error } = useNotify()
  const { errorProps, showError } = useHttpError()

  //Enums Starts

  //Enums Ends

  //Form definition Starts

  //Form definitions ends

  // Handlers Starts
  // Handlers Ends

  // Functions Starts

  const onEditProductHandler = (rowData: ProductsTableModel) => {
    history.push(NonAuthRoutes.ProductsEdit, { clientType: clientType, productId: rowData.id })
  }

  const onViewPriceComponent = (rowData: ProductsTableModel) => {
    history.push(NonAuthRoutes.ProductsPrice, {
      productId: rowData.id,
      commodity: commodity,
      backPageTitle: t('products:title'),
      productTitle: rowData.code
    })
  }

  const onDisableProductHandler = async (selectedRow: ProductsTableModel) => {
    await getConfirmation(ConfirmationDialogs.PRODUCT_DISABLE).then(async () => {
      let newProductDetail: ProductDetail = mapProductDetail(selectedRow)
      newProductDetail.lgEnabled = false

      // call selected product info to ProductModel
      await updateProductDetail(newProductDetail).then(async response => {
        if (response.success) {
          info(t('products:table.rowCustomActions.disable.save.success'))
          // update table view after saving
          fetchProductsList({
            clientType,
            commodity,
            includeInactives: showOnlyActives ? false : true
          })
        } else {
          error(t('products:table.rowCustomActions.disable.save.error'))
        }
      })

      // close Confirmation
      closeConfirmation()
    })
  }

  const onEnableProductHandler = async (selectedRow: ProductsTableModel) => {
    await getConfirmation(ConfirmationDialogs.PRODUCT_ENABLE).then(async () => {
      let newProductDetail: ProductDetail = mapProductDetail(selectedRow)
      newProductDetail.lgEnabled = true

      // call selected product info to ProductModel
      await updateProductDetail(newProductDetail).then(async response => {
        if (response.success) {
          info(t('products:table.rowCustomActions.enable.save.success'))

          // update table view after saving
          fetchProductsList({
            clientType,
            commodity,
            includeInactives: showOnlyActives ? false : true
          })
        } else {
          error(t('products:table.rowCustomActions.enable.save.error'))
        }
      })

      // close Confirmation
      closeConfirmation()
    })
  }

  const onSelectCommodityHandler = (commodity: EnumCommodity): void => {
    setCommodity(commodity)
    storeCommodityFilterForCurrentPage(commodity)
    storeActiveCommodity(commodity)
  }

  const renderCommodities = () => {
    if (commodities !== undefined && commodities.length > 1) {
      return (
        <CommodityFilter
          commodities={[
            { commodity: enumValueToKey(EnumCommodity, commodities[0]) },
            { commodity: enumValueToKey(EnumCommodity, commodities[1]) }
          ]}
          commodity={commodity}
          enabled={!isFetching}
          inactive={false}
          onSelectCommodity={onSelectCommodityHandler}
        />
      )
    }
    return
  }

  const onChangeIsActive = (event: any) => {
    setShowOnlyActives(!event.target.checked)
    fetchProductsList({ clientType, commodity, includeInactives: event.target.checked })
    storeShowOnlyActivesFilterForCurrentPage(!event.target.checked)
  }

  const fetchProductsList = async ({
    commodity,
    clientType,
    includeInactives
  }: {
    commodity: EnumCommodity
    clientType: EnumClientTypes
    includeInactives: boolean
  }) => {
    setIsFetching(true)
    return await fetchProducts({
      commodity,
      clientType,
      includeInactives: includeInactives
    })
      .then(response => {
        if (response.success) {
          setProducts(response.data)
        } else {
          showError({ errorCode: response.error?.code, title: 'Error obteniendo productos' })
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  // const completeDataTableActions = (item: InvoicesTableModel): InvoicesTableModel => {
  //   return {
  //     ...item,
  //     actions:
  //       item.actionsConfiguration &&
  //       item.actionsConfiguration.length > 0 &&
  //       generateIconsFromConfiguration(
  //         item,
  //         filterApplicableActionsConfiguration(item, item.actionsConfiguration),
  //         new Map<ActionIcons, Function>([])
  //       )
  //   }
  // }

  // Functions Ends

  // DataTable Config Starts

  const columns = React.useMemo((): DataTableColumn[] => {
    return [
      {
        name: 'id',
        display: false
      },
      {
        name: 'actions',
        label: '',
        hideForResponsive: true,
        type: DataType.HTML
      },
      {
        name: 'code',
        label: t('products:table.columns.code'),
        type: DataType.TEXT
      },
      {
        name: 'description',
        label: t('products:table.columns.description'),
        type: DataType.TEXT
      },
      {
        name: 'currency',
        label: t('products:table.columns.currency'),
        selectOptions: enumToSelectOptionArray(EnumCurrency, 'enums:EnumCurrencyLabel'),
        type: DataType.SELECT
      }
      // {
      //   name: 'contractsCount',
      //   label: t('products:table.columns.contractsCount'),
      //   type: DataType.NUMBER,
      //   customDecimalScale: 0
      // }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientType, commodity, activeBalanceZone, activeCompany, showOnlyActives])

  const handleInsertAction = async () => {
    history.push(NonAuthRoutes.ProductsAdd, { clientType: clientType })
    return true
  }

  const onCustomizeProductClickHandler = (rowData: ProductsTableModel) => {
    history.push(NonAuthRoutes.ProductsCustomize, {
      productId: rowData.id,
      clientType: clientType,
      conceptModificationType: ConceptModificationType.PRODUCT
    })
  }

  const options = React.useMemo((): DataTableOptions => {
    return {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      search: true,
      elevation: tableConfiguration.elevation,
      filterType: FilterType.ADVANCED,
      download: true,
      canEdit: false,
      canDelete: false,
      canInsert: true,
      customInsertEditModal: true,
      sortOrder: {
        name: 'id',
        direction: SortDirection.DESC
      },
      pagination: true,
      rowsPerPage: tableConfiguration.rowsPerPage,
      rowsPerPageOptions: tableConfiguration.rowsPerPageOptions,

      analytics: {
        customer: getCustomerDomain(),
        view: t('products:table.analytics')
      },
      onRowInsert: handleInsertAction,
      rowCustomActions: [
        {
          icon: <Edit fontSize="small" />,
          tooltip: t('products:table.tooltips.edit'),
          onClick: rowData => onEditProductHandler(rowData),
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Block fontSize="small" />,
          tooltip: t('products:table.tooltips.disable'),
          onClick: onDisableProductHandler,
          userPermission: UserPermission.EDIT,
          visibleIf: 'showDisableIcon'
        },
        {
          icon: <CheckCircleOutline fontSize="small" />,
          tooltip: t('products:table.tooltips.enable'),
          onClick: onEnableProductHandler,
          userPermission: UserPermission.EDIT,
          visibleIf: 'showEnableIcon'
        },
        {
          icon: <FontAwesomeIcon size="sm" icon={faMoneyCheckDollar as IconProp} />,
          tooltip: t('products:table.tooltips.productPrice'),
          onClick: rowData => onViewPriceComponent(rowData),
          userPermission: UserPermission.EDIT
        },
        {
          userPermission: UserPermission.EDIT,
          icon: <Tune fontSize="small" />,
          tooltip: t('products:table.tooltips.customizeProduct'),
          onClick: rowData => onCustomizeProductClickHandler(rowData)
        }
      ],
      onFilterChange: (filter: ColumnFilter[]) => {
        storeDataTableFilterForCurrentPage(filter)
      },
      onSearchChange: (search: string) => {
        storeDataTableSearchForCurrentPage(search)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [clientType, commodity, activeBalanceZone, activeCompany, showOnlyActives])

  // DataTable Config Ends

  // Hooks Starts

  useEffect(() => {
    fetchProductsList({
      clientType,
      commodity,
      includeInactives: showOnlyActives ? false : true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBalanceZone, commodity])

  // Hooks Ends

  return (
    <>
      <Grid container direction="column" spacing={2}>
        <Grid container item direction="row" style={{ marginLeft: 'auto', gap: '10px' }}>
          <Grid item style={{ marginRight: 'auto' }}>
            <FormControlLabel
              style={{ marginLeft: 8 }}
              control={
                <Switch
                  size="small"
                  checked={!showOnlyActives}
                  onChange={onChangeIsActive}
                  name="unsuscribed"
                  color="primary"
                  disabled={isFetching}
                  inputProps={{ 'aria-label': 'unsuscribed clients' }}
                />
              }
              label={
                <Typography variant="subtitle2" color="secondary">
                  {t('products:filters.showInactives')}
                </Typography>
              }
              disabled={isFetching}
            />
          </Grid>
          <Grid item style={{ marginLeft: 'auto' }}>
            {renderCommodities()}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <DataTable
            data={products}
            columns={columns}
            options={options}
            info={DataTableErrorHandler(errorProps)}
            userPermissions={getUserPermissions(Pages.Client)}
            appliedFilter={props.dataTableFilter}
            appliedSearch={props.dataTableSearch}
          />
        </Grid>
      </Grid>
    </>
  )
}
